import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { TriangleAlert } from "lucide-react";
import { type Dispatch, type UnknownAction } from "redux";

import {
  addTermsAndConditionsTextDetails as addTermsAndConditionsTextDetailsAction,
  listTermsAndConditionsText as listTermsAndConditionsTextAction,
} from "@/actions";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { WFPButton } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { getCountryObjectFromCache } from "@/utils";

import { Form, TERMS_OF_USE_FORM_ID, type TermsOfUseFormValues } from "../Form";
import {
  type EntityId,
  type SetTermsAndConditionsModalOptions,
  type TermsAndConditionsModalOptions,
  type TermsAndConditionsText,
} from "../../types";

interface OwnProps {
  modalOptions?: TermsAndConditionsModalOptions;
  record?: TermsAndConditionsText;
  setModalOptions: SetTermsAndConditionsModalOptions;
}

interface DispatchProps {
  addTermsAndConditionsTextDetails: (values: TermsAndConditionsText) => Promise<unknown>;
  refreshTermsAndConditionsTexts: () => void;
}

type TermsAndConditionsTextEditModalProps = OwnProps & DispatchProps;
type AppDispatch = Dispatch<UnknownAction>;

const CLOSED_MODAL_OPTIONS: TermsAndConditionsModalOptions = {
  id: undefined,
  isOpen: false,
  type: undefined,
};

const dispatchAction = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction);

const dispatchRequest = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction) as unknown as Promise<unknown>;

const getCurrentCountryId = () => {
  const country = getCountryObjectFromCache() as { id?: EntityId | null } | null | undefined;

  return country?.id;
};

export const adaptFormValues = (values: TermsOfUseFormValues): TermsAndConditionsText => ({
  ...values,
  country: getCurrentCountryId(),
});

export const formatInitialValues = (
  record?: TermsAndConditionsText
): Partial<TermsOfUseFormValues> | undefined =>
  record && {
    long_text: record.long_text ?? "",
    short_text: record.short_text ?? "",
    target: (record.target ?? undefined) as TermsOfUseFormValues["target"] | undefined,
  };

export const TermsAndConditionsTextEditModal = ({
  modalOptions = CLOSED_MODAL_OPTIONS,
  record,
  setModalOptions,
  addTermsAndConditionsTextDetails,
  refreshTermsAndConditionsTexts,
}: TermsAndConditionsTextEditModalProps) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [hasSaveError, setHasSaveError] = useState(false);
  const initialValues = useMemo(
    () => (modalOptions.type === "create" ? undefined : formatInitialValues(record)),
    [modalOptions.type, record]
  );

  const handleClose = () => {
    setHasSaveError(false);
    setModalOptions(CLOSED_MODAL_OPTIONS);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen || isSaving) {
      return;
    }

    handleClose();
  };

  const preventCloseWhileSaving = (event: Event) => {
    if (isSaving) {
      event.preventDefault();
    }
  };

  const handleSubmit = async (formValues: TermsOfUseFormValues) => {
    setHasSaveError(false);
    setIsSaving(true);

    // Editing publishes a new version rather than mutating the accepted one, so
    // this is a POST in both modes — the audit trail must stay intact.
    const result = await addTermsAndConditionsTextDetails(adaptFormValues(formValues));

    setIsSaving(false);

    if (!result) {
      setHasSaveError(true);
      return;
    }

    refreshTermsAndConditionsTexts();
    handleClose();
  };

  return (
    <Dialog open={Boolean(modalOptions.isOpen)} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-3xl"
        aria-describedby={undefined}
        showCloseButton={!isSaving}
        onEscapeKeyDown={preventCloseWhileSaving}
        onPointerDownOutside={preventCloseWhileSaving}
      >
        <DialogHeader>
          <DialogTitle>{t("termsAndConditionsText.modal.title")}</DialogTitle>
        </DialogHeader>
        {hasSaveError ? (
          <Alert variant="destructive">
            <TriangleAlert aria-hidden="true" />
            <AlertDescription>{t("errorMessage.title")}</AlertDescription>
          </Alert>
        ) : null}
        <div className="max-h-[65vh] overflow-y-auto">
          <Form
            key={modalOptions.id ?? "add"}
            initialValues={initialValues}
            modalOptions={modalOptions}
            onSubmit={handleSubmit}
          />
        </div>
        <DialogFooter>
          <WFPButton type="button" variant="outline" disabled={isSaving} onClick={handleClose}>
            {t("common.cancel")}
          </WFPButton>
          <WFPButton type="submit" form={TERMS_OF_USE_FORM_ID} disabled={isSaving}>
            {isSaving && <Spinner className="size-4" />}
            {t("common.save")}
          </WFPButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => {
  return {
    addTermsAndConditionsTextDetails: values =>
      dispatchRequest(dispatch, addTermsAndConditionsTextDetailsAction(values)),
    refreshTermsAndConditionsTexts: () =>
      dispatchAction(dispatch, listTermsAndConditionsTextAction()),
  };
};

export const ConnectedTermsAndConditionsTextEditModal = connect(
  null,
  mapDispatchToProps
)(TermsAndConditionsTextEditModal);
